import { isFamilyToken, ASSET_FAMILY } from '@exodus/assets';
import { SwapMode, } from '@exodus/exchange-client-providers';
import { byArguments as makeConcurrentByArguments } from 'make-concurrent';
import getBalance from '../utils/get-balance.js';
import getQuoteFee from '../utils/get-quote-fee.js';
import getQuoteLosses from '../utils/get-quote-losses.js';
import { isExpired } from '../utils/is-expired.js';
import { SWAP_ALL_FEE_TOLERANCE } from './constants.js';
const makeConcurrentByArgs = makeConcurrentByArguments;
const MAX_ITERATIONS = 3;
const CHFV2__PROVIDER_ADDRESS = '0x99870de8ae594e6e8705fc6689e89b4d039af1e2';
const inflateFee = (fee, { iteration } = {}) => {
    const multiplier = Math.pow(1 + SWAP_ALL_FEE_TOLERANCE, iteration || 0);
    return fee.mul(multiplier);
};
function getQuoteId(params) {
    const { fromAssetName, toAssetName, amount, swapMode = SwapMode.ExactIn } = params;
    return `${fromAssetName}-${toAssetName}-${amount.toDefaultString({ unit: false })}-${swapMode}`;
}
function getMaxQuoteId(params) {
    const { fromAssetName, toAssetName, amount, isSendAll } = params;
    return `max-${fromAssetName}-${toAssetName}-${amount.toDefaultString({ unit: false })}-${isSendAll}`;
}
export class ExchangeQuotes {
    #accountStatesAtom;
    #activeWalletAccountAtom;
    #addressProvider;
    #exchangeAssets;
    #exchangeProviders;
    #exchangeQuotesAtom;
    #exchangeFees;
    #pricing;
    #walletAccountsAtom;
    #balancesAtom;
    constructor(params) {
        this.#accountStatesAtom = params.accountStatesAtom;
        this.#activeWalletAccountAtom = params.activeWalletAccountAtom;
        this.#addressProvider = params.addressProvider;
        this.#exchangeAssets = params.exchangeAssets;
        this.#exchangeProviders = params.exchangeProviders;
        this.#exchangeQuotesAtom = params.exchangeQuotesAtom;
        this.#exchangeFees = params.exchangeFees;
        this.#pricing = params.pricingClient;
        this.#walletAccountsAtom = params.walletAccountsAtom;
        this.#balancesAtom = params.balancesAtom;
    }
    getBestQuote = makeConcurrentByArgs(async (params) => {
        const quoteId = getQuoteId(params);
        const quotes = await this.#exchangeQuotesAtom.get();
        const previousQuote = quotes[quoteId];
        if (previousQuote && !isExpired(previousQuote.expiry, params.expiryMarginMs ?? 0))
            return previousQuote;
        let quote;
        try {
            quote = await this.#findBestQuote(params);
        }
        catch (error) {
            if (previousQuote && !isExpired(previousQuote.expiry, params.expiryMarginMs ?? 0))
                return previousQuote;
            throw error;
        }
        if (!quote) {
            if (previousQuote && !isExpired(previousQuote.expiry, params.expiryMarginMs ?? 0))
                return previousQuote;
            return null;
        }
        await this.#exchangeQuotesAtom.set((prev) => ({ ...prev, [quoteId]: quote }));
        return quote;
    }, ([params]) => `${getQuoteId(params)}:${params.expiryMarginMs ?? 'default'}`);
    #getPrices = async (fromAsset, toAsset) => {
        try {
            return await this.#pricing.currentPrice({
                assets: [fromAsset.feeAsset.ticker, fromAsset.ticker, toAsset.ticker],
            });
        }
        catch {
            return {
                [fromAsset.ticker]: { USD: 0 },
                [fromAsset.feeAsset.ticker]: { USD: 0 },
                [toAsset.ticker]: { USD: 0 },
            };
        }
    };
    #findBestQuote = async (params) => {
        const quoteId = getQuoteId(params);
        const { fromAssetName, toAssetName, amount, slippage, isSendAll, swapMode = SwapMode.ExactIn, toAddress: toAddressParam, toWalletAccount: toWalletAccountParam, expiryMarginMs, } = params;
        if (toAddressParam && toWalletAccountParam) {
            throw new Error('Cannot specify both toAddress and toWalletAccount');
        }
        const fromAsset = this.#exchangeAssets.getAsset({ assetName: fromAssetName });
        const toAsset = this.#exchangeAssets.getAsset({ assetName: toAssetName });
        const activeWalletAccount = await this.#activeWalletAccountAtom.get();
        const fromWalletAccount = params.fromWalletAccount || activeWalletAccount;
        const toWalletAccount = toWalletAccountParam || activeWalletAccount;
        const walletBalance = getBalance({
            assetName: fromAssetName,
            balances: await this.#balancesAtom.get(),
            walletAccount: fromWalletAccount,
        });
        const walletAccounts = await this.#walletAccountsAtom.get();
        const fromWalletAccountInstance = walletAccounts[fromWalletAccount];
        const fromAddress = await this.#addressProvider.getReceiveAddress({
            assetName: fromAsset.name,
            walletAccount: fromWalletAccountInstance,
            useCache: true,
        });
        let toAddress;
        if (toAddressParam) {
            if (!toAsset.baseAsset.address.validate(toAddressParam)) {
                throw new Error(`Invalid toAddress for ${toAsset.name}: ${toAddressParam}`);
            }
            toAddress = toAddressParam;
        }
        else {
            const toWalletAccountInstance = walletAccounts[toWalletAccount];
            const address = await this.#addressProvider.getReceiveAddress({
                assetName: toAsset.name,
                walletAccount: toWalletAccountInstance,
                useCache: true,
            });
            toAddress = address.toString();
        }
        const prices = await this.#getPrices(fromAsset, toAsset);
        const getIsBestQuote = async (params) => {
            const { losses, lessQuoteLosses, quote } = params;
            const isSwappableWithFees = this.#canCoverSwapWithFees({
                quote,
                walletBalance,
                isSendAll,
                fromAsset,
                fromAmount: amount,
            });
            if (!lessQuoteLosses) {
                return isSwappableWithFees;
            }
            return losses.lte(lessQuoteLosses) && isSwappableWithFees;
        };
        const quotes = await this.#exchangeProviders.getQuotes({
            amount,
            fromAddress: fromAddress.toString(),
            swapMode,
            getFeeOpts: (params) => {
                const asset = this.#exchangeAssets.getAsset({ assetId: params.assetId });
                const isBaseAsset = asset.baseAsset.name === asset.name;
                const { pidBytes, ...feeOptsParams } = params;
                let txInput;
                if (pidBytes) {
                    const _txInput = isBaseAsset
                        ? []
                        : isFamilyToken({ asset, family: ASSET_FAMILY.EVM })
                            ? asset.contract.transfer.build(params.toAddress?.toLowerCase() || CHFV2__PROVIDER_ADDRESS, params.amount.toBaseString({ unit: false }))
                            : [];
                    txInput = Buffer.from(new Uint8Array([..._txInput, ...params.pidBytes]));
                }
                return this.#exchangeFees.getFeeOpts({
                    ...feeOptsParams,
                    txInput,
                    walletAccount: fromWalletAccount,
                    isExchange: true,
                });
            },
            pairId: `${fromAsset.ticker}_${toAsset.ticker}`,
            slippage,
            toAddress,
            isSendAll,
            expiryMarginMs,
        });
        let currentBestQuoteLosses;
        let currentBestQuote = null;
        for (const quote of quotes) {
            const losses = getQuoteLosses({
                amount,
                fromAsset,
                quote,
                prices,
                toAsset,
                swapMode,
            });
            const isBestQuote = await getIsBestQuote({
                losses,
                lessQuoteLosses: currentBestQuoteLosses,
                quote,
            });
            if (isBestQuote) {
                currentBestQuote = quote;
                currentBestQuoteLosses = losses;
            }
        }
        return currentBestQuote ? { ...currentBestQuote, id: quoteId } : null;
    };
    #canCoverSwapWithFees = ({ quote, walletBalance, isSendAll, fromAsset, fromAmount, }) => {
        const isFromFeeAsset = fromAsset.name === fromAsset.feeAsset.name;
        if (!isSendAll || !isFromFeeAsset)
            return true;
        const fee = getQuoteFee(quote, fromAsset);
        return walletBalance.gte(fromAmount.add(fee));
    };
    getMaxQuote = makeConcurrentByArgs(async (params) => {
        const { amount, fromAssetName, toAssetName, fromWalletAccount, isSendAll, slippage, expiryMarginMs, } = params;
        const fromAsset = this.#exchangeAssets.getAsset({ assetName: fromAssetName });
        const isFromFeeAsset = fromAsset.name === fromAsset.feeAsset.name;
        if (!isFromFeeAsset)
            return this.getBestQuote(params);
        const activeWalletAccount = await this.#activeWalletAccountAtom.get();
        const { value: accountStates } = await this.#accountStatesAtom.get();
        const accountState = accountStates[activeWalletAccount]?.[fromAssetName];
        const rentExemptAmount = accountState?.rentExemptAmount || fromAsset.currency.ZERO;
        const computeFee = (quote, iteration) => {
            const fee = getQuoteFee(quote, fromAsset);
            return inflateFee(fee, { iteration });
        };
        const getQuote = async (amount, isSendAll = false) => this.getBestQuote({
            fromAssetName,
            toAssetName,
            fromWalletAccount,
            amount,
            isSendAll,
            slippage,
            expiryMarginMs,
        });
        let quote = await getQuote(amount, false);
        if (!quote)
            return null;
        if (!isSendAll)
            return quote;
        const getCanUseQuote = (quote) => {
            const fee = getQuoteFee(quote, fromAsset);
            const totalUsed = quote.fromAmount.add(fee);
            const remainingAmount = amount.sub(totalUsed);
            return remainingAmount.isZero || remainingAmount.gte(rentExemptAmount);
        };
        let currentBalance = amount.sub(getQuoteFee(quote, fromAsset));
        for (let i = 0; i < MAX_ITERATIONS; i++) {
            quote = (await getQuote(currentBalance, true)) ?? quote;
            if (getCanUseQuote(quote)) {
                break;
            }
            currentBalance = amount.sub(computeFee(quote, i));
            currentBalance = currentBalance.sub(rentExemptAmount);
        }
        if (!getCanUseQuote(quote))
            return null;
        return quote;
    }, ([params]) => `${getMaxQuoteId(params)}:${params.expiryMarginMs ?? 'default'}`);
}
const exchangeQuotesModuleFactory = (args) => new ExchangeQuotes({ ...args });
const exchangeQuotesModuleDefinition = {
    id: 'exchangeQuotes',
    type: 'module',
    factory: exchangeQuotesModuleFactory,
    dependencies: [
        'accountStatesAtom',
        'activeWalletAccountAtom',
        'addressProvider',
        'exchangeAssets',
        'exchangeProviders',
        'exchangeQuotesAtom',
        'exchangeFees',
        'walletAccountsAtom',
        'pricingClient',
        'balancesAtom',
    ],
};
export default exchangeQuotesModuleDefinition;
